import { useRef, useState, useCallback } from "react";
import { commonPostApi } from "../../utils/apiService.js";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import { generateColumnMetadata, uniqObjWithNumKey } from "../../utils/utils.js";
import { rolesAndPermissionFormRender } from "../../utils/serviceData.js";
import { showToast } from "../../components/common/toast.js";
import { TOAST_TYPE, COMMON_MSG } from "../../constants/toast";

/**
 * Custom hook for managing permissions data
 * Handles fetching, pagination, and state management for permissions table
 * 
 * @param {Object} navigator - Optional navigator state for pagination/sorting (deprecated, child components manage their own)
 * @param {Function} setNavigator - Optional function to update navigator state (deprecated, child components manage their own)
 * @returns {Object} Permissions data and functions
 */
const usePermissionsData = (navigator, setNavigator) => {
  const [columns, setColumns] = useState([]);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initLoading, setInitLoading] = useState(true);
  const [assignNum, setAssignNum] = useState(rolesAndPermissionFormRender);
  const fetchingTableRef = useRef(false);

  const fetchPermissionsTable = useCallback(async (navState, updateNavigator) => {
    // Prevent concurrent calls
    if (fetchingTableRef.current) {
      return Promise.resolve();
    }

    fetchingTableRef.current = true;
    setInitLoading(true);
    setLoading(true);

    try {
      const currentObj = {
        query: {
          page: navState?.page,
          limit: navState?.rows,
          sortFields: navState?.sortFields,
          searchCriteria: navState?.searchCriteria || {},
        },
      };

      // Always fetch permissions list for the permission section
      const permissionsResponse = await commonPostApi(API_ENDPOINTS.GET_ALL_PERMISSIONS_LIST, currentObj);

      const permissionsTableData =
        (permissionsResponse?.data?.data || []).map((item) => ({
          ...item,
          role_names: item.role_names
            ? item.role_names.split(',').map((r) => r.trim())
            : []
        }));

      setAssignNum(uniqObjWithNumKey(permissionsTableData, "role_names"));

      // Update navigator if callback provided (from PermissionSection)
      // If no updateNavigator is provided, navigator state is managed by the caller
      if (updateNavigator) {
        updateNavigator((prev) => ({
          ...prev,
          totalRecords: permissionsResponse?.data?.total || 0,
          page: permissionsResponse?.data?.page || 1,
        }));
      }

      setColumns(
        generateColumnMetadata({
          data: permissionsTableData,
          nonRequireField: ["app_permission_id","total_count"],
        })
      );
      setValues(permissionsTableData);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
    } finally {
      setLoading(false);
      setInitLoading(false);
      fetchingTableRef.current = false;
    }
  }, []);

  return {
    columns,
    values,
    loading,
    initLoading,
    assignNum,
    fetchPermissionsTable,
  };
};

export default usePermissionsData;
